import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query'
import useApi from '../../hooks/useApi';
import { RadioGroup } from '../generic/RadioGroup';
import formatNumberWithZeros from '../../../../common/utils/format/formatNumbersWithZeros.js';
import styled from 'styled-components';

const RadioGroupTitle = styled.h4`
  margin-block: 0.5rem;
`;

const SubGroupContainer = styled.div`
  margin-top: 1rem;
`;

const RadioGroupSelector = ({
  entity,
  filter = '',
  valueField = 'id',
  labelField = 'name',
  icon,
  iconField,
  iconMap,
  parentValue,
  value,
  onChange,
  columns,
  filterValues = {},
  subRadio,
  title,
  t,
  selectedValues = {},
  testId
}) => {
  const { getMe } = useApi();

  // Generate options for subRadio groups
  const generateNumberedOptions = (total) => {
    if (!total) return [];

    return Array.from({ length: total }, (_, i) => {
      const number = i + 1;
      const formattedNumber = formatNumberWithZeros(number, total);
      return {
        value: formattedNumber,
        label: formattedNumber,
        icon: icon
      };
    });
  };

  // Construct filter string from filter configuration
  const constructFilterString = useMemo(() => {
    if (!filter || !Array.isArray(filter)) return '';

    const filterParams = Object.entries(filterValues)
      .map(([param, value]) => value ? `${param}:${value}` : '')
      .filter(Boolean);

    return filterParams.length > 0 ? filterParams.join(';') : '';
  }, [filter, filterValues]);

  // First, fetch the parent data if needed
  const { data: parentData } = useQuery({
    queryKey: ['parent', entity],
    queryFn: () => getMe(entity.split('/')[0]),
    enabled: Boolean(entity?.includes(':id')),
    staleTime: 30000,
  });

  // Construct the endpoint based on entity type and parent data
  const endpoint = useMemo(() => {
    if (!entity) return '';

    if (entity.includes(':id') && parentValue) {
      const [baseEntity, , subEntity] = entity.split('/');
      const parent = parentData?.data?.find(item => item.code === parentValue);
      if (!parent) return null;

      const endpoint = `${baseEntity}/${parent.id}/${subEntity}`;
      return constructFilterString ? `${endpoint}?filter=${constructFilterString}` : endpoint;
    }

    return constructFilterString ? `${entity}?filter=${constructFilterString}` : entity;
  }, [entity, parentValue, parentData, constructFilterString]);

  // Fetch the actual data
  const { data, isLoading, error } = useQuery({
    queryKey: [entity, parentValue, constructFilterString],
    queryFn: () => getMe(endpoint),
    enabled: Boolean(entity && (!entity.includes(':id') || (parentValue && endpoint))),
    staleTime: 30000,
  });

  // Resolve per-item icon using iconField + iconMap, falling back to default icon
  const resolveIcon = (item) => {
    if (iconField && iconMap && item[iconField]) {
      return iconMap[item[iconField]] ?? icon;
    }
    return icon;
  };

  // Get options from API data
  const options = data?.data?.map(item => ({
    value: item[valueField],
    label: item[labelField],
    icon: resolveIcon(item),
    originalData: item
  })) || [];

  // Find the selected item's data
  const selectedItem = value ? options.find(opt => opt.value === value)?.originalData : null;

  // Fetch WorkCenter when the selected rack is linked to one
  const { data: workCenterData } = useQuery({
    queryKey: ['workCenter', selectedItem?.workCenterId],
    queryFn: () => getMe(`workCenter/${selectedItem.workCenterId}`),
    enabled: Boolean(selectedItem?.workCenterId),
    staleTime: 30000,
  });

  if (entity?.includes(':id') && !parentValue) {
    return null;
  }

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading data</div>;

  // Render subgroups if they exist and main group has a value
  const renderSubGroups = () => {
    if (!subRadio || !value || !selectedItem) return null;

    return (
      <SubGroupContainer>
        {subRadio.map((subGroup, idx) => {
          // WorkCenter rack: skip level, show WorkCenter positions instead
          if (selectedItem.workCenterId) {
            if (subGroup.valueField === 'totalLevels') return null;

            if (subGroup.valueField === 'totalPositions') {
              const wcLocations = workCenterData?.data?.locations || [];
              if (wcLocations.length === 0) return null;

              const subOptions = wcLocations.map(loc => ({
                value: loc.code,
                label: loc.code,
                icon: icon
              }));

              return (
                <div key={idx}>
                  <RadioGroupTitle>{t(subGroup.title)}</RadioGroupTitle>
                  <RadioGroup
                    value={selectedValues[subGroup.id]}
                    onChange={(newValue) => onChange(newValue, subGroup.id)}
                    options={subOptions}
                    columns={subGroup.columnCount || 4}
                    groupId={subGroup.idPrefix}
                    testId={`${subGroup.id}`}
                  />
                </div>
              );
            }
          }

          // Normal rack: generate numbered options from totalLevels / totalPositions
          const total = selectedItem[subGroup.valueField];
          if (!total) return null;

          const subOptions = generateNumberedOptions(total);
          if (subOptions.length === 0) return null;

          return (
            <div key={idx}>
              <RadioGroupTitle>{t(subGroup.title)}</RadioGroupTitle>
              <RadioGroup
                value={selectedValues[subGroup.id]}
                onChange={(newValue) => onChange(newValue, subGroup.id)}
                options={subOptions}
                columns={subGroup.columnCount || 4}
                groupId={subGroup.idPrefix}
                testId={`${subGroup.id}`}
              />
            </div>
          );
        })}
      </SubGroupContainer>
    );
  };

  // Only render if there are options
  if (options.length === 0) return null;

  return (
    <>
      <RadioGroupTitle>{t(title)}</RadioGroupTitle>
      <RadioGroup
        value={value}
        onChange={onChange}
        options={options}
        columns={columns}
        groupId={entity}
        testId={testId}
      />
      {renderSubGroups()}
    </>
  );
};

export default RadioGroupSelector;
